
import { Component, Prop, Watch } from 'vue-property-decorator';
import BaseComponent from '@/author/components/BaseComponent.vue';
import { KNOWLEDGE_CHECK_EVENTS as KCE, INTERACTIONS } from '@/author/constants';
import KnowledgeCheckEvents from '@/author/components/KnowledgeCheckParts/KnowledgeCheckEvents.vue';
import QuizOption from './QuizOption.vue';
import { shuffle } from './helper/Array';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import { getModule } from 'vuex-module-decorators';
import Timer from 'easytimer.js';
import { Interaction } from '@bob-the-builder/kb4-scorm-client/dist/tsc/src/types/LmsInteraction';
import { differenceInSeconds } from 'date-fns';
import { customTermsFilter } from '@/utils/customTermChecker';
import ConvertMilliSecondsIntoSCORM2004Time from '@/utils/convertTimeScorm';
import { hashString } from '@/utils/strings';
const AppState = getModule(AppModule, store);

interface Options {
  [key: string]: Option;
}
interface Option {
  right: boolean;
  uuid: number;
  textContent : string;
}

@Component({methods: {customTermsFilter}})
export default class Quiz extends BaseComponent {
  @Prop() question!: string;
  @Prop() image!: string;
  @Prop({ default: 'fixed' }) layout!: string;

  @Prop({ default: false }) scorable!: boolean;
  @Prop({ default: false }) instantFeedback!: boolean;
  @Prop() quizInstructions!: string;
  @Prop() accessibilityQuizInstructions!: string;

  @Prop({ required: true }) correctFeedback!: string;
  @Prop({ required: true }) incorrectFeedback!: string;
  @Prop({ default: false }) suppressUnlock!: boolean;
  @Prop({ default: false }) suppressLock!: boolean;
  @Prop({ default: false }) noFeedback !: boolean;
  @Prop() showRedFlagsLabel!: string;
  @Prop({default:false}) forceRedFlagsReview!: boolean;
  @Prop() correctRedFlagsMessage!: string;
  @Prop() incorrectRedFlagsMessage!: string;
  @Prop() themes!: string;

  @Prop() nextLabel!: string;
  @Prop() resultsLabel!: string;
  @Prop() submitLabel!: string;
  @Prop() tryAgainLabel!: string;
  @Prop() learnMoreLabel?: string;
  @Prop(String) correctQuestionTitle!: string;
  @Prop(String) incorrectQuestionTitle!: string;
  @Prop({default: true}) enableFinalFeedback !: boolean;

  // This following props will be used on the QuizOption.vue using $parent.$props()
  @Prop() accessibilityCorrectFeedback!: string;
  @Prop() accessibilityIncorrectFeedback!: string;
  @Prop() accessibilitySelectedFeedback!: string; // DEPRECATED
  @Prop() accessibilitySelectFeedback!: string;
  @Prop() indexNumber?: number;
  @Prop({ default: true }) blocking!: boolean;
  @Prop() random!: boolean;
  @Prop() optionsSrc!: QuizOption[];
  @Prop({default: 1}) weight!: number;
  
  hasLearnMoreContent :boolean = false;
  showLearnMoreContent :boolean = false;
  showLearnMoreButton :boolean = false;
  isInteractive :boolean = true;
  selectedOptions: number[] = [];
  answers!: number[];
  options!: Options;
  showFeedback: boolean = false;
  hasScored: boolean = false;
  multiple: boolean = false;
  partOfFolderSrcPool: boolean = false;
  showPortal: boolean = true;
  isLastQuestion = false;
  quizPoolProps: any | undefined = undefined;
  sessionTimer : Timer = new Timer();
  quizPoolResultsLabel: string | undefined = undefined;

  hasVideo: boolean = false;
  
  lmInteractionStartTime: Date = new Date()
  lmsInteraction :Interaction | Record<string,undefined> = {};
  learnerResponses :Option[] = [];
  weighting!:number;

  created() {
    this.options = {};
    this.answers = [];
    KnowledgeCheckEvents.$on(KCE.OPTION_MOUNTED, this.setupOption);
    KnowledgeCheckEvents.$on(KCE.AFTER_OPTION_COMPLETED_MOUNTED, this.setupTextContent);
  }
  private setupTextContent(option: any){ 
    const option_uid=(option)._uid;
    this.options[option_uid]={
      ...this.options[option_uid],
      textContent: option.textContent
    };
  }
  private setupOption(option: any) {

    const option_uid=(option)._uid;
    let index= Object.keys(this.options).length;
    if(index === 0)
      option.isFirstOption = true;
    
    this.options[option_uid]={
      right: option.$props.right!==undefined&&option.$props.right!==false,
      uuid: index,
      textContent: option.textContent
    };
    if(this.options[option_uid].right) 
      this.answers.push(index);

    this.multiple=this.answers.length>1;
    this.$emit(KCE.AFTER_OPTION_MOUNTED,this.multiple);
    KnowledgeCheckEvents.$emit(KCE.AFTER_OPTION_MOUNTED,this.multiple);
    if (Boolean(this.$slots['learn-more']?.length)) {
      this.hasLearnMoreContent = true;
      KnowledgeCheckEvents.$emit(KCE.HAS_LEARN_MORE_CONTENT);
    }
    
    option.$on('arrowEvent', this.handleArrowFocus);
    option.$on('tabEvent', this.handleOptionTab);

    this.setAnswersAndType();
  }

  handleArrowFocus(data: { event: Event, direction: string }): void {
    const { event, direction } = data;
    const parentElement = (event?.target as any).parentElement?.parentElement;
    const allOptions = parentElement?.parentElement?.querySelectorAll('.button.h-stack');

    let nextButton: HTMLElement | null = null;
    if (direction === 'next') {
      const nextOption = parentElement?.nextElementSibling as HTMLElement;
      nextButton = nextOption?.querySelector('.button.h-stack');
      if (!nextButton) nextButton = parentElement?.parentElement?.parentElement?.querySelector('.button.h-stack');

    } else if (direction === 'previous') {
      const prevOption = parentElement?.previousElementSibling as HTMLElement;
      nextButton = prevOption?.querySelector('.button.h-stack');
      if (!nextButton) {
        const lastOption = allOptions?.[allOptions.length - 1] as HTMLElement;
        nextButton = lastOption;
      }
    }
    if (nextButton) nextButton.focus();

  }
  
  handleOptionTab(event: any) {
    // set focus to controls
    let nextButton = document.getElementById('submitQuiz');
    if (nextButton) nextButton.focus();

  }
  mounted() {
    KnowledgeCheckEvents.$on(KCE.OPTION_SELECTED, (option: any) => this.handleOptionClick(option._uid, option));

    if (!this.suppressLock && !this.getSavedData?.completed) 
      this.lockForward();
    
    KnowledgeCheckEvents.$on(
      KCE.TURN_INTO_FOLDER_POOL,
      this.setQuizPoolProps
    );
    KnowledgeCheckEvents.$on(
      KCE.IS_LAST_QUESTION,
      (isLastQuestion: boolean) => {
        this.isLastQuestion = isLastQuestion;
      }
    );
    KnowledgeCheckEvents.$emit(KCE.MOUNTED);
  
    let tDate = new Date();
    this.sessionTimer.start();
    
    this.lmsInteraction.id = `${INTERACTIONS.URN_QUIZ}-${AppState.getCurrentIndex}-${hashString(this.question)}`;
    this.lmsInteraction.description = customTermsFilter(this.question);
    this.weighting = 0+this.weight;
    if(this.isScorable()) 
      this.lmsInteraction.weighting = `${this.weighting}`;
    else  
      this.lmsInteraction.weighting = '0'; 
    this.lmsInteraction.timestamp = new Date(tDate.getTime() - (tDate.getTimezoneOffset() * 60000)).toISOString().slice(0,-5);
    this.setAnswersAndType();
  }

  setAnswersAndType() {
    try { 
      this.lmsInteraction.type = 'choice';
      if (this.multiple)
        this.lmsInteraction.description = `${this.lmsInteraction.description}`;
    } catch (error) {
      throw error;
    }
  }

  setLatency() {
    const now = new Date();
    this.lmsInteraction.latency = String(ConvertMilliSecondsIntoSCORM2004Time(now.getTime() - this.lmInteractionStartTime.getTime()));
  }

  setQuizPoolProps(props: any) :void {
    this.partOfFolderSrcPool = true;
    this.quizPoolResultsLabel = props.resultsLabel;
    this.quizPoolProps = props;
    this.weighting = 1/props.quantity;
    if (props.scorable)
      this.lock();
  }

  get getLearnMoreLabel() :string{
    if (!this.hasLearnMoreContent) return '';
    return this.learnMoreLabel || this.$options?.filters?.localize('Learn More');
  }

  get isMobileApplication() :boolean {
    return this.isMobileApp();
  }

  get quizOptions() {
    return this.random ? this.shuffleOptions(this.optionsSrc) : this.optionsSrc;
  }

  get hasInstantFeedback () :boolean {
    return this.instantFeedback || this.hasLearnMoreContent;
  }

  shuffleOptions(options: QuizOption[]): QuizOption[] {
    return shuffle([...options]).sort((a, b) => a.order - b.order);
  }

  get hasControlOptions() {
    return !!this.$slots['control-options'];
  }

  beforeDestroy() {
    KnowledgeCheckEvents.$off([
      KCE.OPTION_MOUNTED,
      KCE.OPTION_SELECTED,
      KCE.TURN_INTO_FOLDER_POOL,
      KCE.IS_LAST_QUESTION
    ]);
  }

  handleOptionClick(uid:number, optionEl?:any) {
    if (!this.lmsInteraction.latency) this.setLatency();
    const option = this.options[uid];
    if(!option) return;
    // if it's not multiple and has learnMoreContent, the selection cannot be changed 
    if (!this.multiple && this.selectedOptions.length > 0 && this.hasLearnMoreContent) return;

    if (!this.multiple){
      this.selectedOptions = [];
      this.learnerResponses= [];
    }
    const positionIndex = this.selectedOptions.indexOf(option.uuid);
    const elementExistsInArray = positionIndex > -1;
    if (elementExistsInArray && !this.hasLearnMoreContent) {
      // element exists in array. It means at this point that it should be removed
      // but if LearnMore is active, element cannot be removed
      this.selectedOptions.splice(positionIndex, 1);
      this.learnerResponses.splice(positionIndex, 1);
      return;
    }
    if (!elementExistsInArray) {
      this.selectedOptions.push(option.uuid);
      this.learnerResponses.push(option);
    }

    if (this.hasInstantFeedback || this.hasControlOptions) this.shouldEvaluateAnswer();
    // Showing portal only if the quiz option have a custom feedback
    this.showPortal = optionEl.hasCustomFeedback;
  }

  handleCompleteRedFlags(){
    if(!this.partOfFolderSrcPool)
      this.unlock();
  }
  get answersRole(): string {
    if(!!this.$slots['control-options'])
      return '';
    return this.multiple ? 'list' : 'radiogroup';
  }

  get isBlocking(): boolean {
    if (this.partOfFolderSrcPool) return true;
    if (this.blocking && !this.answeredCorrectly()) return true;
    return this.suppressUnlock;
  }

  handleSubmitClick() {
    if (!this.isBlocking && !this.isScorable()) this.unlock();
    this.showFeedback = true;
    if (this.isScorable()) this.setQuizScore();
    if (this.hasInstantFeedback || this.hasControlOptions) this.shouldEvaluateAnswer();
  }

  showQuizFeedback() {
    this.lmsInteraction.correct_responses = Object.keys(this.options).filter((key :string) => this.options[key].right).map((key :string) => `${this.options[key].textContent}`);
    this.lmsInteraction.correct_responses_index = Object.keys(this.options).filter((key :string) => this.options[key].right).map((key :string) => `${this.options[key].uuid + 1}`);

    this.lmsInteraction.learner_response = this.learnerResponses.map((option)=> `${option.textContent}`).join('[,]');
    this.lmsInteraction.learner_response_index = this.learnerResponses.map((option)=> `${option.uuid + 1}`).join('[,]');

    this.lmsInteraction.result = this.answeredCorrectly() ? 'correct' : 'incorrect';
    if(this.isScorable())
      this.recordInteraction((this.lmsInteraction as Interaction));

    KnowledgeCheckEvents.$emit(KCE.SHOWING_FEEDBACK, this.answeredCorrectly());

    if(this.noFeedback && !this.isFinalFeedbackEnabled) {
      if(this.isLastQuestion) return;
      return this.handleNext();
    };

    this.showFeedback = true;

    var nextElementId = this.hasControlOptions ? 'optionControlShowFlags' :'feedbackQuiz';
    setTimeout(() => {
      let feedback = document.getElementById(nextElementId);
      if (feedback) 
        feedback.focus();
    }, 200);

    setTimeout(()=>{
      this.hasVideo = this.checkVideo();
    },500);
    this.sessionTimer.reset();
  }

  shouldEvaluateAnswer() {
    if (this.showLearnMoreButton) {
      this.showLearnMoreButton = false;
      this.showLearnMoreContent = true;
    }
    if (this.hasLearnMoreContent && !this.multiple) {
      this.evaluateAnswer();
      if (this.isScorable()) this.setQuizScore();
      return;
    }
    if (this.hasInstantFeedback && !this.allRightAnswersAreSelected()) return;
    if (this.isScorable()) this.setQuizScore();
    if (this.hasInstantFeedback)
      this.evaluateAnswer();
    else
      this.showQuizFeedback();
    if (this.suppressUnlock || this.isBlocking || this.partOfFolderSrcPool) return;
    if (this.forceRedFlagsReview && !this.answeredCorrectly()) return;
    this.unlock();
  }

  allRightAnswersAreSelected() :boolean{
    return this.answers.every(val => this.selectedOptions.indexOf(val) > -1);
  }

  evaluateAnswer() {
    this.saveData(true);
    this.showLearnMoreButton = true;
    if (this.hasLearnMoreContent)
      KnowledgeCheckEvents.$emit(KCE.HAS_EVALUATED_ANSWER);
    if (this.isLastQuestion && !this.isFinalFeedbackEnabled) this.unlock();
    if (this.suppressUnlock) return;
    this.unlock();
    
  }

  answeredCorrectly() {
    return (
      this.selectedOptions.every((selected) => {
        return this.answers.indexOf(selected) > -1;
      }
      ) && this.selectedOptions.length == this.answers.length
    );
  }

  setQuizScore() {
    if (!this.hasScored && this.isScorable()) {
      this.hasScored = true;
      this.chooseAnswer(this.answeredCorrectly());
    }
  }

  get isSubmitDisabled(): boolean {
    return Boolean(
      (this.indexNumber && this.indexNumber > this.$parent?.$data.indexedModal)
      || this.showFeedback ||this.selectedOptions.length === 0
    );
  }

  resetQuiz() {
    this.showFeedback = false;
    this.saveData({});
    setTimeout(() => {
      let quizQuestion = document.getElementById('quizQuestion');
      if (quizQuestion) 
        quizQuestion.focus();
      
    }, 200);
  }

  handleNext() {
    KnowledgeCheckEvents.$emit(KCE.NEXT);
  }

  handleFinalFeedback() {
    KnowledgeCheckEvents.$emit(KCE.SHOW_FINAL_FEEDBACK);
  }

  hasQuizPoolProps(property: string): boolean {
    return (
      this.quizPoolProps !== undefined &&
      this.quizPoolProps.hasOwnProperty(property)
    );
  }

  isScorable() :boolean{
    return Boolean(this.quizPoolProps?.scorable || this.scorable);
  }

  get isFinalFeedbackEnabled() {
    return this.hasQuizPoolProps('enableFinalFeedback')
      ? this.quizPoolProps.enableFinalFeedback
      : this.enableFinalFeedback;
  }

  get getResultsLabel() {
    if(this.partOfFolderSrcPool) return this.quizPoolResultsLabel;
    return this.resultsLabel !== undefined && this.resultsLabel.trim() !== ''
      ? this.resultsLabel
      : this.nextLabel;
  }

  get haveSlotedFeedback (){
    if(this.showPortal)
      return true;
    return ((!!this.$slots['feedback-correct-message'] && this.answeredCorrectly()) || (!!this.$slots['feedback-incorrect-message'] && !this.answeredCorrectly()));
  }
  checkVideo(){
    return !!document.getElementById('VideoPlayer');
  }
  get hasCustomFeedback() :boolean{

    if(!!this.$slots['feedback-incorrect-message'] && this.hasVideo)
      return true;

    if(this.showPortal)
      return true;
    return false;

  }
  get quizClassNames() {
    return { 
      showingCustomFeedback : !!this.$slots['feedback-correct-message'] && this.hasVideo && this.showFeedback && !this.hasControlOptions, 
      showingCustomFeedbackSlot : this.showFeedback && this.haveSlotedFeedback, 
      hideContent: this.showFeedback && !this.hasControlOptions,
      hideHead: this.showFeedback && !this.hasControlOptions,
      hideControls: this.showFeedback && !this.hasControlOptions, 
    };
  }

  get shouldShowKnowledgeCheckControls() :boolean {
    return !this.instantFeedback || this.hasLearnMoreContent;
  }
}
